import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Cover } from "types"
import { fileToBase64, getImageFromFile } from "utils/logicWithFiles"
import { compressImageWithLoading } from "utils/imageCompression"
import { COMPRESSION_CONFIG } from "../../../../config/compressionConfig"
import {
  Box,
  Button,
  Card,
  CardAction,
  CardAsset,
  CardHeader,
  IconButton,
  Loader,
  Typography,
} from "@strapi/design-system"
import { Trash } from "@strapi/icons"

interface UploadGlassesImageCardProps {
  onChange: (data: Cover | null) => void
  file?: Cover
  isLeft?: boolean
  isRight?: boolean
  isMiddle?: boolean
  fileTypeValidation?: string
  isLoading?: boolean
}

export const UploadGlassesImageCard = ({
  onChange,
  file,
  fileTypeValidation,
  isLeft = false,
  isRight = false,
  isLoading = false,
}: UploadGlassesImageCardProps) => {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [hover, setHover] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCompressing, setIsCompressing] = useState(false)
  const [compressionProgress, setCompressionProgress] = useState(0)
  const title = isLeft
    ? t("uploadGlassesImageCard.leftTemplate")
    : isRight
    ? t("uploadGlassesImageCard.rightTemplate")
    : t("uploadGlassesImageCard.bodyTemplate")

  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const onCapture = async (e: React.ChangeEvent<HTMLInputElement> | null) => {
    const file = e?.target?.files?.[0]
    if (file) {
      if (fileTypeValidation && file.type !== fileTypeValidation) {
        setError(
          t("uploadGlassesImageCard.invalidFileType", {
            fileType: fileTypeValidation.split("/")[1],
          })
        )
        return
      }

      setIsCompressing(true)
      setCompressionProgress(0)
      setError(null)

      try {
        const result = await compressImageWithLoading(
          file,
          (progress) => setCompressionProgress(progress),
          COMPRESSION_CONFIG
        )

        const compressedFile = result.compressedFile
        const fileBase64 = await fileToBase64(compressedFile)

        getImageFromFile(compressedFile, ({ src, width, height }) => {
          const uploadedFile = {
            name: compressedFile.name,
            type: compressedFile.type.split("/")[1].toUpperCase(),
            width,
            height,
            src,
          }
          setData(uploadedFile)

          onChange({
            fileBase64,
            lastModified: compressedFile.lastModified,
            name: compressedFile.name,
            size: compressedFile.size,
            type: compressedFile.type,
          })
        })
      } catch (error) {
        console.error("Compression failed:", error)
        const fileBase64 = await fileToBase64(file)

        getImageFromFile(file, ({ src, width, height }) => {
          const uploadedFile = {
            name: file.name,
            type: file.type.split("/")[1].toUpperCase(),
            width,
            height,
            src,
          }
          setData(uploadedFile)

          onChange({
            fileBase64,
            lastModified: file.lastModified,
            name: file.name,
            size: file.size,
            type: file.type,
          })
        })
      } finally {
        setIsCompressing(false)
        setCompressionProgress(0)
        e.target.value = ""
      }
    } else {
      onChange(null)
    }
  }

  useEffect(() => {
    setData({
      ...file,
      filaBase64: null,
      src: file?.fileBase64,
    })
  }, [file])

  const onDeleteHandler = () => {
    setData(null)
    onChange(null)
  }

  return (
    <Box
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      className="relative w-[270px] h-[170px] !bg-transparent "
    >
      {error && (
        <Box>
          <Typography variant="omega" className="text-danger600">
            {error}
          </Typography>
        </Box>
      )}
      <Box className="w-[270px] h-[170px] flex items-center justify-center shadow-none">
        <Box
          className={`absolute top-0 left-0 w-full h-full ${
            !data?.src ? "flex" : "hidden"
          } items-center flex-col justify-center z-10 gap-2 shadow-none`}
        >
          {isLoading || isCompressing ? (
            <Box className="flex flex-col items-center justify-center gap-2">
              <Loader />
              {isCompressing && (
                <Typography variant="omega" className="!text-gray-600 text-center">
                  {t("uploadGlassesImageCard.compressing")} {compressionProgress}%
                </Typography>
              )}
            </Box>
          ) : null}
          {!isLoading && !isCompressing ? (
            <>
              <Typography
                variant="omega"
                className="!text-gray-600 text-center"
                style={{
                  maxWidth: "240px",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  lineHeight: "1.4",
                }}
              >
                {t("uploadGlassesImageCard.addImagePrefix")}{" "}
                <Typography
                  variant="omega"
                  className="font-semibold !text-gray-600"
                  style={{ display: "inline" }}
                >
                  {title}
                </Typography>{" "}
                {t("uploadGlassesImageCard.addImageSuffix")}
              </Typography>

              <Button
                onClick={() => {
                  if (!isCompressing) hiddenFileInput.current?.click()
                }}
                disabled={isCompressing}
              >
                <input
                  value=""
                  id={title.toLowerCase()}
                  ref={hiddenFileInput}
                  onChange={onCapture}
                  accept="image/png"
                  type="file"
                  hidden
                  multiple={false}
                  disabled={isCompressing}
                />
                {t("uploadGlassesImageCard.selectImage")}
              </Button>
            </>
          ) : null}
        </Box>

        {data?.src && !isLoading && !isCompressing ? (
          <Card className="w-[270px] h-[170px] flex items-center justify-center shadow-none border-none">
            {hover ? (
              <CardAction className="right-4">
                <IconButton
                  label={t("uploadGlassesImageCard.editLabel")}
                  onClick={onDeleteHandler}
                  className="cursor-pointer z-10"
                >
                  <Trash />
                </IconButton>
              </CardAction>
            ) : null}

            <CardHeader className="border-none border-b-0">
              <CardAsset
                src={data?.src}
                className={`bg-${data?.src ? "white" : "inherit"} border-none`}
              />
            </CardHeader>
          </Card>
        ) : (
          <Box className="w-[255px] h-[170px] flex items-center justify-center !bg-none shadow-none rounded !border !border-gray-300" />
        )}

        {(isLoading || isCompressing) && data?.src ? (
          <Box className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white bg-opacity-75 z-20">
            <Box className="flex flex-col items-center justify-center gap-2">
              <Loader />
              {isCompressing && (
                <Typography variant="omega" className="!text-gray-600 text-center">
                  {t("uploadGlassesImageCard.compressing")} {compressionProgress}%
                </Typography>
              )}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
